import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input, Textarea, Select } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, BookOpen, Pencil, Search, ShieldCheck, Flag } from "lucide-react";

const UNITS = ["each", "sq ft", "lin ft", "cu yd", "hour", "month", "set", "opening", "quote", "markup", "cost+20%"];
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const blank = { name: "", category: "", description: "", client_price: "", contractor_cost: "", materials: "", disposal: "", unit: "each", national_benchmark: "", requires_approval: false };

const marginColor = (pct) => (pct < 15 ? "text-red-400" : pct < 30 ? "text-yellow-400" : "text-emerald-400");

export default function PriceBook() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const load = useCallback(() => api.get("/pricebook").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/pricebook/categories").then((r) => setCats(r.data)).catch(() => {}); }, [load]);

  const filtered = useMemo(() => items.filter((it) =>
    (cat === "all" || it.category === cat) &&
    (!q || `${it.name} ${it.description} ${it.category}`.toLowerCase().includes(q.toLowerCase()))
  ), [items, cat, q]);

  const grouped = useMemo(() => {
    const m = {};
    filtered.forEach((it) => { (m[it.category || "General"] = m[it.category || "General"] || []).push(it); });
    return Object.entries(m);
  }, [filtered]);

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: edit.name.trim(), category: (edit.category || "General").trim(), description: edit.description || "",
      client_price: parseFloat(edit.client_price) || 0, contractor_cost: parseFloat(edit.contractor_cost) || 0,
      materials: parseFloat(edit.materials) || 0, disposal: parseFloat(edit.disposal) || 0,
      unit: edit.unit || "each", national_benchmark: edit.national_benchmark || "", requires_approval: !!edit.requires_approval,
    };
    try {
      if (edit.id) await api.put(`/pricebook/${edit.id}`, payload);
      else await api.post("/pricebook", payload);
      toast.success("Price item saved"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    try { await api.delete(`/pricebook/${confirmDel.id}`); toast.success("Removed"); setConfirmDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const liveMargin = edit ? ((parseFloat(edit.client_price) || 0) - (parseFloat(edit.contractor_cost) || 0) - (parseFloat(edit.materials) || 0) - (parseFloat(edit.disposal) || 0)) : 0;
  const livePct = edit && parseFloat(edit.client_price) ? (liveMargin / parseFloat(edit.client_price)) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{isAdmin ? "Master Price Book — margin protected" : "Standard Rates"}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{isAdmin ? "Price Book" : "Service Prices"}</h1>
        </div>
        {isAdmin && <Btn data-testid="add-price-btn" onClick={() => setEdit({ ...blank })}><Plus size={16} /> Add Item</Btn>}
      </div>

      <Card className="p-4 border-l-4 border-[#FF5722]" data-testid="pricebook-payment-note">
        <div className="flex items-start gap-2">
          <Flag size={16} className="text-[#FF5722] mt-0.5 shrink-0" />
          <p className="text-sm text-zinc-300"><span className="text-white font-semibold">Pay varies per client.</span> Contractors are paid no more than <span className="text-white font-semibold">45 days</span> after the work order is completed with <span className="text-white font-semibold">all required photos and checklist items</span>. All tasks must be completed within the timeframe or the job may be <span className="text-white font-semibold">reassigned</span> and <span className="text-white font-semibold">late fees</span> applied.</p>
        </div>
      </Card>

      {!isAdmin && (
        <Card className="p-4 border-[#FF5722]/40" data-testid="price-client-note">
          <p className="text-sm text-zinc-300">These are our <span className="text-white font-semibold">standard suggested rates</span> to help you estimate costs before you request service. Your final quote is confirmed by our team after review.</p>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input data-testid="price-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="pl-9" />
        </div>
        <div className="w-full sm:w-64">
          <Select data-testid="price-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="all">All categories ({items.length})</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </div>
      </div>

      {grouped.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><BookOpen className="inline mr-2" size={16} />No price items found.</Card>
      ) : grouped.map(([group, rows]) => (
        <div key={group} className="space-y-2">
          <div className="font-head font-bold uppercase text-sm tracking-wide text-[#FF5722]">{group} <span className="text-zinc-600 font-mono text-xs">({rows.length})</span></div>
          <Card className="p-0 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-zinc-900 border-b-2 border-[#FF5722] text-[#FF5722]">
                  <th className="p-3 font-mono text-xs uppercase tracking-wider">Service</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider">Unit</th>
                  <th className="p-3 font-mono text-xs uppercase tracking-wider text-right">{isAdmin ? "Client $" : "Price"}</th>
                  {isAdmin && <th className="p-3 font-mono text-xs uppercase tracking-wider text-right hidden md:table-cell">Contractor $</th>}
                  {isAdmin && <th className="p-3 font-mono text-xs uppercase tracking-wider text-right hidden lg:table-cell">Mat.</th>}
                  {isAdmin && <th className="p-3 font-mono text-xs uppercase tracking-wider text-right hidden lg:table-cell">Disp.</th>}
                  {isAdmin && <th className="p-3 font-mono text-xs uppercase tracking-wider text-right">Margin</th>}
                  {isAdmin && <th className="p-3"></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <tr key={it.id} data-testid={`price-row-${it.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60 transition-colors align-top">
                    <td className="p-3">
                      <div className="text-white font-semibold flex items-center gap-1.5">{it.name}
                        {it.requires_approval && <span title="Requires approval" className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase bg-yellow-500/15 text-yellow-400 border border-yellow-700 px-1"><Flag size={9} /> Approve</span>}
                      </div>
                      {it.description && <div className="text-zinc-500 text-xs mt-0.5">{it.description}</div>}
                      {isAdmin && it.national_benchmark && <div className="text-zinc-600 font-mono text-[10px] mt-0.5 flex items-center gap-1"><ShieldCheck size={10} /> {it.national_benchmark}</div>}
                    </td>
                    <td className="p-3 font-mono text-zinc-500 text-xs whitespace-nowrap">{it.unit}</td>
                    <td className="p-3 text-right font-mono text-[#FF5722] font-bold whitespace-nowrap">{money(it.client_price)}</td>
                    {isAdmin && <td className="p-3 text-right font-mono text-zinc-300 hidden md:table-cell whitespace-nowrap">{money(it.contractor_cost)}</td>}
                    {isAdmin && <td className="p-3 text-right font-mono text-zinc-500 hidden lg:table-cell whitespace-nowrap">{money(it.materials)}</td>}
                    {isAdmin && <td className="p-3 text-right font-mono text-zinc-500 hidden lg:table-cell whitespace-nowrap">{money(it.disposal)}</td>}
                    {isAdmin && <td className={`p-3 text-right font-mono font-bold whitespace-nowrap ${marginColor(it.margin_pct)}`}>{money(it.gross_margin)}<div className="text-[10px] font-normal">{it.margin_pct}%</div></td>}
                    {isAdmin && (
                      <td className="p-3 text-right whitespace-nowrap">
                        <button data-testid={`edit-price-${it.id}`} onClick={() => setEdit({ ...blank, ...it })} className="text-zinc-400 hover:text-[#FF5722] mr-2"><Pencil size={15} /></button>
                        <button data-testid={`del-price-${it.id}`} onClick={() => setConfirmDel(it)} className="text-red-500 hover:text-red-400"><Trash2 size={15} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {/* Add / Edit modal */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black uppercase">{edit?.id ? "Edit Price Item" : "Add Price Item"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div><Label>Service name *</Label><Input data-testid="price-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label><Input data-testid="price-category" list="pb-cats" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} placeholder="e.g. Securing" /><datalist id="pb-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist></div>
                <div><Label>Unit</Label><Select data-testid="price-unit" value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</Select></div>
              </div>
              <div><Label>Description</Label><Textarea data-testid="price-desc" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>SSS Client Charge / Bid $</Label><Input data-testid="price-client" type="number" step="0.01" value={edit.client_price} onChange={(e) => setEdit({ ...edit, client_price: e.target.value })} /></div>
                <div><Label>Target Contractor Cost $</Label><Input data-testid="price-contractor" type="number" step="0.01" value={edit.contractor_cost} onChange={(e) => setEdit({ ...edit, contractor_cost: e.target.value })} /></div>
                <div><Label>Materials $</Label><Input data-testid="price-materials" type="number" step="0.01" value={edit.materials} onChange={(e) => setEdit({ ...edit, materials: e.target.value })} /></div>
                <div><Label>Disposal / Trip $</Label><Input data-testid="price-disposal" type="number" step="0.01" value={edit.disposal} onChange={(e) => setEdit({ ...edit, disposal: e.target.value })} /></div>
              </div>
              <Card className="p-3 bg-zinc-950 flex items-center justify-between" data-testid="price-margin-preview">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">SSS Gross Margin</span>
                <span className={`font-head font-black text-xl ${marginColor(livePct)}`}>{money(liveMargin)} <span className="text-sm">({livePct.toFixed(1)}%)</span></span>
              </Card>
              <div><Label>National benchmark (HUD / Fannie reference)</Label><Input data-testid="price-benchmark" value={edit.national_benchmark} onChange={(e) => setEdit({ ...edit, national_benchmark: e.target.value })} placeholder="e.g. HUD: $150 door/opening" /></div>
              <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="price-approval" checked={!!edit.requires_approval} onChange={(e) => setEdit({ ...edit, requires_approval: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" /> Requires approval before dispatch</label>
              <Btn data-testid="price-submit-btn" onClick={save} className="w-full">Save Item</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#141417] border-2 border-red-600 text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase text-red-400">Delete item?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">Remove <span className="text-white font-semibold">{confirmDel?.name}</span> from the price book?</p>
          <div className="flex gap-2 justify-end mt-2">
            <Btn variant="secondary" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" data-testid="confirm-del-price" onClick={remove}>Yes, delete</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
